import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Typography, styled } from '@mui/material';
import { type GridColDef } from '@mui/x-data-grid-premium';

import { Utils, useSettings } from 'app/main/components';
import type { DetailsSectionInfoContentProps, ViewFooterCustomInfoProps } from 'app/main/components/page-layouts';
import DetailsSectionTable from 'app/main/components/page-layouts/details/sections/table/DetailsSectionTable';
import DetailsSectionTableInputCells from 'app/main/components/page-layouts/details/sections/table/DetailsSectionTableInputCells';
import {
	renderCellWithTooltip,
	renderCurrency,
	renderDate
} from 'app/main/components/page-layouts/view/table/ViewTableUtils';

type InvoicesDeadlinesSectionProps = DetailsSectionInfoContentProps & { newRowBaseModel?: any };

const StyledViewTable = styled(DetailsSectionTable)(({ theme }) => ({
	'& .InvoicesDeadlinesTab-ForcedAmount': {
		color: theme.palette.warning.main
	}
}));

export default function InvoicesDetailsSectionDeadlines(props: InvoicesDeadlinesSectionProps) {
	const { t } = useSettings();

	const columns: GridColDef[] = [
		{
			field: 'indl_d_deadline',
			headerName: t('INVOICES_DEADLINES_COLUMN_DATE_DEADLINE'),
			minWidth: 120,
			renderCell: renderDate,
			editable: true,
			renderEditCell: params => <DetailsSectionTableInputCells.DateEditInputCell {...params} />
		},
		{
			field: 'indl_d_payment',
			headerName: t('INVOICES_DEADLINES_COLUMN_DATE_PAYMENT'),
			minWidth: 120,
			renderCell: renderDate,
			editable: true,
			renderEditCell: params => <DetailsSectionTableInputCells.DateEditInputCell {...params} />
		},
		{
			field: 'indl_amount',
			type: 'number',
			headerName: t('INVOICES_DEADLINES_COLUMN_AMOUNT'),
			minWidth: 150,
			renderCell: renderCurrency,
			editable: true,
			renderEditCell: params => <DetailsSectionTableInputCells.CurrencyEditInputCell {...params} />
		},
		{
			field: 'indl_amount_payment',
			type: 'number',
			headerName: t('INVOICES_DEADLINES_COLUMN_AMOUNT_PAYMENT'),
			minWidth: 150,
			cellClassName: ({ row }) => (row.indl_force_close ? 'InvoicesDeadlinesTab-ForcedAmount' : ''),
			renderCell: renderCurrency,
			editable: true,
			renderEditCell: params => <DetailsSectionTableInputCells.CurrencyEditInputCell {...params} />
		},
		{
			field: 'indl_pame_id',
			headerName: t('INVOICES_DEADLINES_COLUMN_PAYMENT_TYPE'),
			minWidth: 200,
			renderCell: ({ row }) => renderCellWithTooltip({ value: row.pame_title }),
			editable: true,
			renderEditCell: params => (
				<DetailsSectionTableInputCells.SearchSelectEditInputCell systable="paymentmethods" {...params} />
			)
		},
		{
			field: 'indl_notes',
			type: 'string',
			headerName: t('INVOICES_DEADLINES_COLUMN_NOTES'),
			minWidth: 200,
			flex: 1,
			renderCell: renderCellWithTooltip,
			editable: true,
			renderEditCell: params => <DetailsSectionTableInputCells.TextEditInputCell {...params} rows={4} />
		},
		{
			field: 'indl_idsb_id',
			headerName: t('INVOICES_DEADLINES_COLUMN_SUBTYPE'),
			minWidth: 150,
			renderCell: ({ row }) => renderCellWithTooltip({ value: row.idsb_title }),
			editable: true,
			renderEditCell: params => (
				<DetailsSectionTableInputCells.SearchSelectEditInputCell
					systable="invoices_deadlines_subtypes"
					{...params}
				/>
			)
		},
		{
			field: 'indl_force_close',
			type: 'boolean',
			headerName: t('INVOICES_DEADLINES_COLUMN_FORCE_CLOSE'),
			minWidth: 120,
			editable: true
		}
	];

	function FooterCustomInfo({ items }: ViewFooterCustomInfoProps) {
		const totAmountFinal = Utils.calcObjectsTotal(items || [], 'indl_amount');
		const totAmountResidual = (props.model.invc_amount_tax_incl || 0) - totAmountFinal;

		return (
			<div className="h-full p-4">
				<Typography variant="subtitle2">
					<FormattedMessage
						id="invoicesDeadlinesSection.totAmountResidual"
						defaultMessage={t('INVOICES_DEADLINES_SECTION_TOT_AMOUNT_DEADLINE_RESIDUAL')}
						values={{ value: Utils.formatCurrency(totAmountResidual) }}
					/>
				</Typography>
			</div>
		);
	}

	return (
		<StyledViewTable
			name="invc_deadlines"
			columns={columns}
			idField="indl_uid"
			rows={props.model.invc_deadlines || []}
			slots={{ footerCustomInfo: FooterCustomInfo }}
			isEdit={props.isEdit}
			sendChange={props.sendChange}
			editProps={{ editMode: 'row', newRowBaseModel: props.newRowBaseModel }}
			disabledAdd={!props.model.invc_uid}
		/>
	);
}
